import { IBusLocation, IBusStopEvent } from "@/domain/entities/lineInfo";
import { BusLocation } from "@/domain/entities/lineInfo/busLocation";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { BusStopEventCode, LineTypeCode } from "@/types";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import dayjs from "dayjs";
import {
  HttpBusLocation,
  HttpBusStopEventCode,
  HttpBusStopEvent,
  IBusLocationParser,
} from "../lineInfoRepository.types";

function convertEventCode(code?: HttpBusStopEventCode): BusStopEventCode {
  switch (code) {
    case HttpBusStopEventCode.Enter:
      return BusStopEventCode.Enter;

    case HttpBusStopEventCode.Arrive:
      return BusStopEventCode.Arrive;

    case HttpBusStopEventCode.Leave:
      return BusStopEventCode.Leave;

    default:
      return BusStopEventCode.None;
  }
}

function findIndexOfLastBusStopEvent(events: HttpBusStopEvent[]): number {
  let lastIndex = -1;
  let latestTimestamp: dayjs.Dayjs | null = null;

  events.forEach((event, index) => {
    const eventCode = convertEventCode(event.stationEventCode);
    const eventTimestamp = dayjs(event.collectedTs);
    if (
      eventCode !== BusStopEventCode.None &&
      (latestTimestamp === null || eventTimestamp.isAfter(latestTimestamp))
    ) {
      latestTimestamp = eventTimestamp;
      lastIndex = index;
    }
  });

  return lastIndex;
}

function parseBusStopEventCode(
  httpBusStopEvents: HttpBusStopEvent[]
): BusStopEventCode[] {
  // 원본 데이터를 IBusStopEvent로 변환한 리스트
  const busStopEventCodes = httpBusStopEvents.map((event) =>
    convertEventCode(event.stationEventCode)
  );
  // UI 표현을 위해 조정된 값을 넣을 리스트 (마지막 이벤트만 남기고 나머지는 BusStopEventCode None 처리)
  const arrangedBusStopEventCodes = Array(busStopEventCodes.length).fill(
    BusStopEventCode.None
  );
  const lastEventIndex = findIndexOfLastBusStopEvent(httpBusStopEvents);
  const event = busStopEventCodes[lastEventIndex];

  if (lastEventIndex === 0) {
    // 첫번째 정류장 이벤트의 진입 이벤트는 별도 처리
    arrangedBusStopEventCodes[lastEventIndex] =
      lastEventIndex === 0 && event === BusStopEventCode.Enter
        ? BusStopEventCode.FirstEnter
        : event;
  } else if (lastEventIndex === busStopEventCodes.length - 1) {
    // 마지막 정류장 이벤트의 진출 이벤트는 별도 처리
    arrangedBusStopEventCodes[lastEventIndex] =
      lastEventIndex === busStopEventCodes.length - 1 &&
      event === BusStopEventCode.Leave
        ? BusStopEventCode.LastLeave
        : event;
  } else {
    arrangedBusStopEventCodes[lastEventIndex] = event;
  }

  return arrangedBusStopEventCodes;
}

function parseBusStopEvent(events: HttpBusStopEvent[]): IBusStopEvent[] {
  const busStopEvents: IBusStopEvent[] = [];
  const busStopEventCodes = parseBusStopEventCode(events);

  events.forEach((event, index) => {
    const busStopEvent: IBusStopEvent = {
      uid: event.uid, // 정류장 식별자
      name: event.name, // 정류장 이름
      shortName: event.shortName, // 정류장 단축이름
      lat: event.lat, // 정류장 위도
      lng: event.lng, // 정류장 경도
      bearing: event.bearing,
      arrivalTime: event.arrivalTime, // 정류장 도착 예정 시각 (조기 출발 고려 안함)
      departureTime: event.departureTime, // 정류장 출발 예정 시각 (조기 출발 고려 안함)
      minutes: event.minutes,
      collectedTs: event.collectedTs, // 이벤트 수집 일시 (2022-05-02T16:10:10.828+09:00)
      busStopEventCode: busStopEventCodes[index], // 정류장 이벤트. Enter:진입, Arrive:도착, Leave:진출
      prevIntervalMin: event.prevIntervalMin, // 이전 정류장과의 분단위 간격
    };
    busStopEvents.push(busStopEvent);
  });
  return busStopEvents;
}

function findNextStopName(
  lineTypeCode: string,
  httpBusStopEvents: HttpBusStopEvent[]
) {
  if (!httpBusStopEvents.length) return "";

  const lastEventIndex = findIndexOfLastBusStopEvent(httpBusStopEvents);

  if (lastEventIndex === httpBusStopEvents.length - 1) {
    const isCircular =
      lineTypeCode === LineTypeCode.Circular ||
      lineTypeCode === LineTypeCode.Inside;

    if (isCircular) {
      const isSameFirstAndLastStop =
        httpBusStopEvents[0].uid === httpBusStopEvents[lastEventIndex].uid;
      return isSameFirstAndLastStop ? httpBusStopEvents[1].name : "";
    }
    return "";
  } else {
    return httpBusStopEvents[lastEventIndex + 1].name;
  }
}

function filterBusStopEvents(
  busStopEvents: HttpBusStopEvent[]
): HttpBusStopEvent[] {
  return busStopEvents.filter((event) => event.displayYn ?? true);
}

@injectable()
export class BusLocationParser implements IBusLocationParser {
  constructor() {}

  toDomain(location: HttpBusLocation): Result<IBusLocation, ParseError> {
    try {
      const busLocationData: IBusLocation = {
        allocUid: location.allocUid,
        lineTurnUid: location.lineTurnUid,
        lineRouteUid: location.lineRouteUid,
        lineTypeCode: location.lineTypeCode,
        totalSeatsCount: location.lastSeat?.seatCount ?? -1,
        remainingSeatsCount: location.lastSeat?.remainCount ?? -1,
        boardingCount: location.lastSeat?.boardCount ?? -1,
        busOrder: location.busOrder,
        plateNumber: location.carNo ?? "",
        driveDate: location.driveDate,
        collectedTime: location.collectedTs,
        lat: location.lastLocation?.lat ?? -1,
        lng: location.lastLocation?.lng ?? -1,
        bearing: location.lastLocation?.bearing,
        driverEventCode: location.lastLocation?.driveEventCode,
        driverEventName: location.lastLocation?.driveEventName,
        allocStatusCode: location.allocStatusCode,
        busStopEvents: parseBusStopEvent(
          filterBusStopEvents(location.stationEvents ?? [])
        ),
        nextStopName: findNextStopName(
          location.lineTypeCode,
          location.stationEvents ?? []
        ),
      };
      const busLocation = new BusLocation(busLocationData);
      return ok(busLocation);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
